import {
  type AdminDeviceKpiQuery,
  ADMIN_JOB_STATUSES,
  ADMIN_JOB_TYPES,
  ADMIN_DEFAULT_TIME_RANGE_DAYS,
  ADMIN_FULL_HISTORY_TIME_RANGE_START_AT,
  ADMIN_LIST_QUERY_DEFAULT_LIMIT,
  ADMIN_LIST_QUERY_MAX_LIMIT,
  ADMIN_PLATFORMS,
  ADMIN_RECOGNITION_DOCUMENT_KINDS,
  ADMIN_RECOGNITION_OUTCOMES,
  ADMIN_RECOGNITION_RUN_STATUSES,
  type AdminFailedFlowQuery,
  type AdminJobQuery,
  type AdminJobStatus,
  type AdminJobType,
  type AdminPlatform,
  type AdminRecognitionAuditQuery,
  type AdminRecognitionDocumentKind,
  type AdminRecognitionOutcome,
  type AdminRecognitionRunStatus,
  type AdminReleaseRegressionQuery,
  type AdminTimeRange,
  type AdminUserLookupInput,
} from "./admin-read-models";

function normalizeOptionalQueryValue(value: string | null): string | undefined {
  if (value === null) {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function parseOptionalInt(value: string | null): number | undefined {
  const normalized = normalizeOptionalQueryValue(value);

  if (normalized === undefined) {
    return undefined;
  }

  const parsed = Number.parseInt(normalized, 10);
  return Number.isNaN(parsed) ? undefined : parsed;
}

function clampListLimit(limit: number | undefined): number {
  if (limit === undefined || limit <= 0) {
    return ADMIN_LIST_QUERY_DEFAULT_LIMIT;
  }

  return Math.min(limit, ADMIN_LIST_QUERY_MAX_LIMIT);
}

function parseEnumValue<T extends string>(
  value: string | null,
  allowedValues: readonly T[],
): T | undefined {
  const normalized = normalizeOptionalQueryValue(value);
  return normalized !== undefined && allowedValues.includes(normalized as T)
    ? (normalized as T)
    : undefined;
}

function parseEnumArrayValues<T extends string>(
  params: URLSearchParams,
  keys: readonly string[],
  allowedValues: readonly T[],
): readonly T[] {
  const values = new Set<T>();

  for (const key of keys) {
    for (const rawValue of params.getAll(key)) {
      for (const part of rawValue.split(",")) {
        const normalized = normalizeOptionalQueryValue(part);

        if (normalized !== undefined && allowedValues.includes(normalized as T)) {
          values.add(normalized as T);
        }
      }
    }
  }

  return [...values];
}

export function createDefaultAdminTimeRange(now = new Date()): AdminTimeRange {
  return {
    from: new Date(now.getTime() - ADMIN_DEFAULT_TIME_RANGE_DAYS * 24 * 60 * 60 * 1000).toISOString(),
    to: now.toISOString(),
  };
}

export function createDefaultAdminListTimeRange(now = new Date()): AdminTimeRange {
  return {
    from: ADMIN_FULL_HISTORY_TIME_RANGE_START_AT,
    to: now.toISOString(),
  };
}

function toIsoStringOrFallback(value: string | undefined, fallback: string): string {
  return value ?? fallback;
}

export function parseAdminTimeRangeParams(
  params: URLSearchParams,
  defaultTimeRange = createDefaultAdminTimeRange(),
): AdminTimeRange {
  const from = normalizeOptionalQueryValue(params.get("from"));
  const to = normalizeOptionalQueryValue(params.get("to"));

  if (from === undefined && to === undefined) {
    return defaultTimeRange;
  }

  if (from !== undefined && to === undefined) {
    return {
      from,
      to: defaultTimeRange.to,
    };
  }

  if (from === undefined && to !== undefined) {
    const toDate = new Date(to);
    return {
      from: new Date(toDate.getTime() - ADMIN_DEFAULT_TIME_RANGE_DAYS * 24 * 60 * 60 * 1000).toISOString(),
      to,
    };
  }

  return {
    from: toIsoStringOrFallback(from, defaultTimeRange.from),
    to: toIsoStringOrFallback(to, defaultTimeRange.to),
  };
}

export function parseFailedFlowQueryParams(
  params: URLSearchParams,
  defaultTimeRange = createDefaultAdminTimeRange(),
): AdminFailedFlowQuery {
  return {
    timeRange: parseAdminTimeRangeParams(params, defaultTimeRange),
    feature: normalizeOptionalQueryValue(params.get("feature")),
    useCase: normalizeOptionalQueryValue(params.get("useCase")),
    errorCode: normalizeOptionalQueryValue(params.get("errorCode")),
    statusCode: parseOptionalInt(params.get("statusCode")),
    requestId: normalizeOptionalQueryValue(params.get("requestId")),
    correlationId: normalizeOptionalQueryValue(params.get("correlationId")),
    userIdHash: normalizeOptionalQueryValue(params.get("userIdHash")),
    petIdHash: normalizeOptionalQueryValue(params.get("petIdHash")),
    release: normalizeOptionalQueryValue(params.get("release")),
    appVersion: normalizeOptionalQueryValue(params.get("appVersion")),
    platform: parseEnumValue(params.get("platform"), ADMIN_PLATFORMS),
    deviceInstallId: normalizeOptionalQueryValue(params.get("deviceInstallId")),
    cursor: normalizeOptionalQueryValue(params.get("cursor")),
    limit: clampListLimit(parseOptionalInt(params.get("limit"))),
  };
}

export function parseDeviceKpiQueryParams(
  params: URLSearchParams,
  defaultTimeRange = createDefaultAdminTimeRange(),
): AdminDeviceKpiQuery {
  return {
    timeRange: parseAdminTimeRangeParams(params, defaultTimeRange),
    release: normalizeOptionalQueryValue(params.get("release")),
    appVersion: normalizeOptionalQueryValue(params.get("appVersion")),
    platform: parseEnumValue(params.get("platform"), ADMIN_PLATFORMS),
    deviceInstallId: normalizeOptionalQueryValue(params.get("deviceInstallId")),
    cursor: normalizeOptionalQueryValue(params.get("cursor")),
    limit: clampListLimit(parseOptionalInt(params.get("limit"))),
  };
}

export function parseUserLookupParams(
  params: URLSearchParams,
  routeParams: Record<string, string | undefined>,
): AdminUserLookupInput {
  return {
    internalUserId:
      normalizeOptionalQueryValue(routeParams.internalUserId ?? null) ??
      normalizeOptionalQueryValue(params.get("internalUserId")),
    userIdHash:
      normalizeOptionalQueryValue(routeParams.userIdHash ?? null) ??
      normalizeOptionalQueryValue(params.get("userIdHash")),
    requestId: normalizeOptionalQueryValue(params.get("requestId")),
    correlationId: normalizeOptionalQueryValue(params.get("correlationId")),
  };
}

function parseJobType(value: string | null): AdminJobType | undefined {
  return parseEnumValue(value, ADMIN_JOB_TYPES);
}

function parseJobStatus(value: string | null): AdminJobStatus | undefined {
  return parseEnumValue(value, ADMIN_JOB_STATUSES);
}

function parsePlatform(value: string | null): AdminPlatform | undefined {
  return parseEnumValue(value, ADMIN_PLATFORMS);
}

export function parseJobQueryParams(
  params: URLSearchParams,
  defaultTimeRange = createDefaultAdminTimeRange(),
): AdminJobQuery {
  return {
    timeRange: parseAdminTimeRangeParams(params, defaultTimeRange),
    jobType: parseJobType(params.get("jobType")),
    status: parseJobStatus(params.get("status")),
    userIdHash: normalizeOptionalQueryValue(params.get("userIdHash")),
    petIdHash: normalizeOptionalQueryValue(params.get("petIdHash")),
    linkedFileId: normalizeOptionalQueryValue(params.get("linkedFileId")),
    linkedRecordId: normalizeOptionalQueryValue(params.get("linkedRecordId")),
    requestId: normalizeOptionalQueryValue(params.get("requestId")),
    correlationId: normalizeOptionalQueryValue(params.get("correlationId")),
    release: normalizeOptionalQueryValue(params.get("release")),
    appVersion: normalizeOptionalQueryValue(params.get("appVersion")),
    cursor: normalizeOptionalQueryValue(params.get("cursor")),
    limit: clampListLimit(parseOptionalInt(params.get("limit"))),
  };
}

export function parseReleaseRegressionQueryParams(
  params: URLSearchParams,
  defaultTimeRange = createDefaultAdminTimeRange(),
): AdminReleaseRegressionQuery {
  return {
    timeRange: parseAdminTimeRangeParams(params, defaultTimeRange),
    release: normalizeOptionalQueryValue(params.get("release")),
    appVersion: normalizeOptionalQueryValue(params.get("appVersion")),
    platform: parsePlatform(params.get("platform")),
    cursor: normalizeOptionalQueryValue(params.get("cursor")),
    limit: clampListLimit(parseOptionalInt(params.get("limit"))),
  };
}

function parseRecognitionDocumentKinds(params: URLSearchParams): readonly AdminRecognitionDocumentKind[] {
  return parseEnumArrayValues(params, ["documentKind"], ADMIN_RECOGNITION_DOCUMENT_KINDS);
}

function parseRecognitionStatuses(params: URLSearchParams): readonly AdminRecognitionRunStatus[] {
  return parseEnumArrayValues(params, ["status"], ADMIN_RECOGNITION_RUN_STATUSES);
}

function parseRecognitionOutcomes(params: URLSearchParams): readonly AdminRecognitionOutcome[] {
  return parseEnumArrayValues(params, ["outcome"], ADMIN_RECOGNITION_OUTCOMES);
}

function parseRecognitionSort(value: string | null): AdminRecognitionAuditQuery["sort"] {
  const parsed = parseEnumValue(value, ["startedAt.desc", "startedAt.asc"] as const);
  return parsed ?? "startedAt.desc";
}

function parseRecognitionSourceFreshness(
  value: string | null,
): AdminRecognitionAuditQuery["sourceFreshness"] {
  return parseEnumValue(value, ["live", "snapshot"] as const);
}

export function parseRecognitionAuditQueryParams(
  searchParams: URLSearchParams,
  defaultTimeRange = createDefaultAdminListTimeRange(),
): AdminRecognitionAuditQuery {
  return {
    timeRange: parseAdminTimeRangeParams(searchParams, defaultTimeRange),
    accountId: normalizeOptionalQueryValue(searchParams.get("accountId")),
    petId: normalizeOptionalQueryValue(searchParams.get("petId")),
    documentKinds: parseRecognitionDocumentKinds(searchParams),
    statuses: parseRecognitionStatuses(searchParams),
    outcomes: parseRecognitionOutcomes(searchParams),
    failedStage: normalizeOptionalQueryValue(searchParams.get("failedStage")),
    errorCode: normalizeOptionalQueryValue(searchParams.get("errorCode")),
    aiProvider: normalizeOptionalQueryValue(searchParams.get("aiProvider")),
    aiModel: normalizeOptionalQueryValue(searchParams.get("aiModel")),
    sourceFreshness: parseRecognitionSourceFreshness(searchParams.get("sourceFreshness")),
    limit: clampListLimit(parseOptionalInt(searchParams.get("limit"))),
    cursor: normalizeOptionalQueryValue(searchParams.get("cursor")),
    sort: parseRecognitionSort(searchParams.get("sort")),
  };
}
